import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchOrders } from './ordersSlice';
import Shell from '../dashboard/Shell';
import CreateOrderModal from './CreateOrderModal';
import RecordSaleModal from './RecordSaleModal';
import CheckoutLinksTab from '../sales-tools/CheckoutLinksTab';
import SendCheckoutLinkSidebar from '../sales-tools/SendCheckoutLinkSidebar';
import SendPaymentLinkPopover from '../sales-tools/SendPaymentLinkPopover';
import OrderRulesNudge from '../sales-tools/OrderRulesNudge';
import InlinePopover from '../../components/ui/InlinePopover';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import './Orders.css';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'processing', label: 'Processing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const getStatusClass = (status) => {
  const s = (status || '').toLowerCase();
  return ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'].includes(s) ? s : 'pending';
};

const getStatusLabel = (status) => {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getPaymentClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'refunded') return 'refunded';
  return 'unpaid';
};

const formatRelativeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function Orders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const { getAxios } = useSecureAuth();
  const toast = useToast();
  const {
    items = [],
    appointments = [],
    status,
    error,
    total,
    appointmentsTotal,
    totalItems,
    totalPages,
    page: currentPage,
    stats
  } = useSelector(s => s.orders);

  // Phase 3.5 — section + status tab persisted in URL so back/forward
  // restore the merchant's view. ?section=orders|checkout-links and
  // ?status=all|pending|… are read on mount; setters update both
  // local state AND the URL (replaceState so the browser history
  // doesn't get a separate entry for every tab click).
  const [activeTab, setActiveTabState] = useState(() => {
    const raw = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search).get('status') : null;
    return raw && STATUS_TABS.some((t) => t.key === raw) ? raw : 'all';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [section, setSectionState] = useState(() => {
    const raw = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search).get('section') : null;
    return raw === 'checkout-links' ? 'checkout-links' : 'orders';
  });

  // Wrapper setters that mirror state into the URL via
  // window.history.replaceState (avoids polluting browser history
  // with one entry per tab click). useLocation is imported so the
  // component re-renders if the URL changes by other means.
  // eslint-disable-next-line no-unused-vars
  const _location = useLocation();
  const setActiveTab = useCallback((next) => {
    setActiveTabState(next);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (next === 'all') url.searchParams.delete('status');
      else url.searchParams.set('status', next);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);
  const setSection = useCallback((next) => {
    setSectionState(next);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (next === 'orders') url.searchParams.delete('section');
      else url.searchParams.set('section', next);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);
  // Phase 3.2 — Send checkout link wizard. Locked-state visual on the
  // toolbar button is the upgrade signal; merchants discover the
  // upgrade path through the Checkout links tab's inline UpgradeInline.
  const [showSendSidebar, setShowSendSidebar] = useState(false);
  const { hasAccess: hasCheckoutLinks } = useFeatureAccess('sales.checkout_link');
  const [createdLinksBump, setCreatedLinksBump] = useState(0);
  const handleOpenSend = useCallback(() => {
    // Toolbar button is disabled on FREE — this only fires for PRO+.
    setShowSendSidebar(true);
  }, []);
  const handleSendCreated = useCallback(() => {
    // Switch to Checkout links tab so the merchant lands on the row
    // they just sent. The tab re-fetches on mount via key bump.
    setSection('checkout-links');
    setCreatedLinksBump((n) => n + 1);
  }, []);

  // Phase 3.3 — Send payment link popover: a single shared popover
  // mounted at the page level, anchored to whichever row's button
  // triggered it. Cleaner than one popover per row (one mount, one
  // outside-click listener).
  const [paymentLinkPopover, setPaymentLinkPopover] = useState(null); // { order, anchorEl } | null
  const openPaymentLinkPopover = useCallback((order, event) => {
    event.stopPropagation();
    setPaymentLinkPopover({ order, anchorEl: event.currentTarget });
  }, []);
  const closePaymentLinkPopover = useCallback(
    () => setPaymentLinkPopover(null),
    []
  );

  // Mobile-card actions popover: a small per-card overflow menu that
  // replaces the bare 'Send payment link' pill at the bottom of every
  // unpaid card. Same shared-popover trick.
  const [cardActionsMenu, setCardActionsMenu] = useState(null); // { order, anchorEl } | null
  const openCardActionsMenu = useCallback((order, event) => {
    event.stopPropagation();
    setCardActionsMenu({ order, anchorEl: event.currentTarget });
  }, []);
  const closeCardActionsMenu = useCallback(
    () => setCardActionsMenu(null),
    []
  );

  const [searchParams] = useSearchParams();
  const deepLinkOrderId = searchParams.get('orderId');

  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showRecordSaleModal, setShowRecordSaleModal] = useState(false);

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [exportingToSheets, setExportingToSheets] = useState(false);

  const [channelFilter, setChannelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchOrders({ page: 1 }));
  }, [dispatch]);

  // Legacy `?orderId=` deep links now redirect to the dedicated detail page.
  useEffect(() => {
    if (deepLinkOrderId) {
      navigate(`/orders/${deepLinkOrderId}`, { replace: true });
    }
  }, [deepLinkOrderId, navigate]);

  useEffect(() => {
    const checkSheetsStatus = async () => {
      try {
        const axios = getAxios();
        const response = await axios.get('/merchant/sheets/oauth/status');
        setSheetsConnected(response.data?.connected || false);
      } catch (err) {
        setSheetsConnected(false);
      }
    };
    checkSheetsStatus();
  }, [getAxios]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportMenu && !e.target.closest('.rsord-export')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu]);

  const handlePageChange = (newPage) => {
    dispatch(fetchOrders({ page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Merge orders with transformed appointments
  const transformedAppointments = appointments.map(appt => {
    const taxAmount = parseFloat(appt.paymentData?.taxAmount) || 0;
    const servicePrice = parseFloat(appt.servicePrice || 0);
    const serviceCharge = parseFloat(appt.serviceCharge || 0);
    return {
      id: appt.id,
      type: 'appointment',
      customer: {
        firstName: appt.customerName?.split(' ')[0] || 'Appointment',
        lastName: appt.customerName?.split(' ').slice(1).join(' ') || 'Customer',
        email: appt.customerEmail || '',
        phone: appt.customerPhone || ''
      },
      orderItems: [{
        product: {
          name: appt.service?.name || appt.serviceName || 'Appointment Service',
          primaryImage: appt.service?.primaryImage
        },
        quantity: 1,
        price: servicePrice
      }],
      subtotal: servicePrice,
      serviceCharge,
      taxTotal: taxAmount,
      taxName: appt.paymentData?.taxSettings?.name || 'VAT',
      total: parseFloat(appt.totalPrice) || (servicePrice + serviceCharge + taxAmount),
      status: appt.status || 'CONFIRMED',
      paymentStatus: appt.paymentStatus || 'PAID',
      createdAt: appt.scheduledAt || appt.createdAt,
      appointmentDate: appt.appointmentDate,
      appointmentTime: appt.appointmentTime
    };
  });

  const allItems = [
    ...items.map(o => ({ ...o, type: o.type || 'order' })),
    ...transformedAppointments
  ];

  const filteredOrders = useMemo(() => {
    return allItems.filter(order => {
      if (!order || !order.id) return false;
      const matchesTab = activeTab === 'all' || order.status?.toUpperCase() === activeTab.toUpperCase();
      const q = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        order.id.toLowerCase().includes(q) ||
        order.customer?.firstName?.toLowerCase().includes(q) ||
        order.customer?.lastName?.toLowerCase().includes(q) ||
        order.customer?.email?.toLowerCase().includes(q);

      let matchesChannel = channelFilter === 'all';
      if (!matchesChannel) {
        if (channelFilter === 'APPOINTMENT') {
          matchesChannel = order.type === 'appointment';
        } else {
          matchesChannel = order.type !== 'appointment' && order.channel?.toUpperCase() === channelFilter.toUpperCase();
        }
      }
      const matchesSource = sourceFilter === 'all' || order.manualSaleSource?.toUpperCase() === sourceFilter.toUpperCase();
      return matchesTab && matchesSearch && matchesChannel && matchesSource;
    });
  }, [allItems, activeTab, searchQuery, channelFilter, sourceFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: allItems.length };
    STATUS_TABS.forEach(tab => {
      if (tab.key !== 'all') {
        counts[tab.key] = allItems.filter(o => o.status?.toUpperCase() === tab.key.toUpperCase()).length;
      }
    });
    return counts;
  }, [allItems]);

  const summaryMetrics = useMemo(() => {
    if (stats?.totalOrders > 0) {
      return {
        totalOrders: stats.totalOrders,
        pendingOrders: stats.pendingOrders,
        completedOrders: stats.completedOrders,
        totalRevenue: stats.totalRevenue
      };
    }
    const totalOrders = total + appointmentsTotal;
    const pendingOrders = allItems.filter(o => ['PENDING', 'CONFIRMED'].includes(o.status?.toUpperCase())).length;
    const completedOrders = allItems.filter(o => o.status?.toUpperCase() === 'DELIVERED').length;
    const totalRevenue = allItems
      .filter(o => o.status?.toUpperCase() === 'DELIVERED' || (o.type === 'appointment' && o.paymentStatus === 'PAID'))
      .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
    return { totalOrders, pendingOrders, completedOrders, totalRevenue };
  }, [stats, total, appointmentsTotal, allItems]);

  const handleViewOrder = (order) => {
    navigate(`/orders/${order.id}`);
  };

  const handleUpdateOrder = (order) => {
    navigate(`/orders/${order.id}`);
  };

  const handleCreateOrderSuccess = (order) => {
    toast.success(`Order ${order.orderNumber} created successfully!`);
    setShowCreateOrderModal(false);
    dispatch(fetchOrders({ page: currentPage }));
  };

  const handleRecordSaleSuccess = (order) => {
    toast.success(`Sale ${order.orderNumber} recorded successfully!`);
    setShowRecordSaleModal(false);
    dispatch(fetchOrders({ page: currentPage }));
  };

  const handleExportCSV = () => {
    const headers = ['Order ID', 'Customer Name', 'Email', 'Phone', 'Status', 'Payment Status', 'Total', 'Items', 'Date'];
    const rows = filteredOrders.map(order => [
      order.id.slice(-8),
      order.customer?.firstName ? `${order.customer.firstName} ${order.customer.lastName || ''}`.trim() : 'Guest',
      order.customer?.email || '',
      order.customer?.phone || '',
      order.status,
      order.paymentStatus || '',
      order.total || 0,
      order.orderItems?.length || 0,
      order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Orders exported successfully!');
    setShowExportMenu(false);
  };

  const handleExportToSheets = async () => {
    if (!sheetsConnected) {
      toast.error('Please connect Google Sheets in Settings > Integrations first');
      return;
    }
    setExportingToSheets(true);
    setShowExportMenu(false);
    try {
      const axios = getAxios();
      const response = await axios.post('/merchant/sheets/export/orders', {
        status: activeTab !== 'all' ? activeTab.toUpperCase() : undefined
      });
      if (response.data?.success) {
        toast.success(
          <div>
            <strong>Exported {response.data.orderCount} orders!</strong>
            <br />
            <a href={response.data.spreadsheetUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0d7463', textDecoration: 'underline' }}>
              Open in Google Sheets
            </a>
          </div>,
          { duration: 8000 }
        );
      } else {
        throw new Error(response.data?.error || 'Export failed');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to export to Google Sheets';
      if (err.response?.data?.featureGated) {
        toast.error('Google Sheets export requires a Pro or Premium subscription');
      } else if (err.response?.data?.reconnect) {
        toast.error('Please reconnect Google Sheets in Settings');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setExportingToSheets(false);
    }
  };

  const hasActiveFilters = channelFilter !== 'all' || sourceFilter !== 'all';

  return (
    <Shell>
      <div className="rsord">
        {/* Header */}
        <header className="rsord__header">
          <div className="rsord__heading">
            <h1 className="rsord__title">Orders</h1>
            <p className="rsord__subtitle">
              {total + appointmentsTotal} total · {appointmentsTotal} {appointmentsTotal === 1 ? 'appointment' : 'appointments'}
            </p>
          </div>

          <div className="rsord__header-actions">
            <button
              type="button"
              className={`rsord__btn rsord__btn--ghost rsord__btn--icon ${status === 'loading' ? 'is-refreshing' : ''}`}
              onClick={() => dispatch(fetchOrders({ page: currentPage }))}
              aria-label="Refresh"
              title="Refresh"
              disabled={status === 'loading'}
            >
              <svg
                className="rsord__refresh-icon"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12a9 9 0 1 1-3.51-7.13" />
                <path d="M21 3v6h-6" />
              </svg>
            </button>

            {/* Consolidated quick-actions menu — Record sale / Send
                checkout link / Export options all live here so the
                header has a single primary CTA instead of 5 buttons. */}
            <div className="rsord-export">
              <button
                type="button"
                className="rsord__btn rsord__btn--ghost"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exportingToSheets}
              >
                {exportingToSheets ? (
                  <span className="rsord__spinner" />
                ) : (
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                )}
                <span className="rsord__btn-label">Quick actions</span>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {showExportMenu && (
                <div className="rsord__menu">
                  <button type="button" className="rsord__menu-item" onClick={() => { setShowExportMenu(false); setShowRecordSaleModal(true); }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                    </svg>
                    <div>
                      <div className="rsord__menu-title">Record sale</div>
                      <div className="rsord__menu-sub">Manual order from any channel</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="rsord__menu-item"
                    onClick={() => { setShowExportMenu(false); if (hasCheckoutLinks) handleOpenSend(); }}
                    disabled={!hasCheckoutLinks}
                    title={!hasCheckoutLinks ? 'Upgrade to PRO to send checkout links' : undefined}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                    </svg>
                    <div className="rsord__menu-grow">
                      <div className="rsord__menu-title">
                        Send checkout link
                        {!hasCheckoutLinks && <span className="sl-pro-pill">PRO</span>}
                      </div>
                      <div className="rsord__menu-sub">Pre-built cart, shareable URL</div>
                    </div>
                  </button>
                  <div className="rsord__menu-divider" />
                  <button type="button" className="rsord__menu-item" onClick={() => { setShowExportMenu(false); handleExportCSV(); }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <div>
                      <div className="rsord__menu-title">Export to CSV</div>
                      <div className="rsord__menu-sub">Download spreadsheet</div>
                    </div>
                  </button>
                  <button type="button" className="rsord__menu-item" onClick={() => { setShowExportMenu(false); handleExportToSheets(); }} disabled={!sheetsConnected}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <line x1="9" y1="3" x2="9" y2="21" />
                      <line x1="15" y1="3" x2="15" y2="21" />
                    </svg>
                    <div>
                      <div className="rsord__menu-title">Export to Google Sheets</div>
                      <div className="rsord__menu-sub">{sheetsConnected ? 'Create in your Drive' : 'Connect in Settings'}</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <button type="button" className="rsord__btn rsord__btn--primary" onClick={() => setShowCreateOrderModal(true)}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>Create order</span>
            </button>
          </div>
        </header>

        {/* Phase 3.4 — Inline nudge pointing to Settings → Order rules.
            Renders only when the policy is disabled AND the merchant
            hasn't dismissed it this session. */}
        <OrderRulesNudge />

        {/* Phase 3.2 — Section switcher (Orders / Checkout links) */}
        <div className="sl-section-switcher" role="tablist" aria-label="Page section">
          <button
            type="button"
            role="tab"
            aria-selected={section === 'orders'}
            className={`sl-section-pill ${section === 'orders' ? 'is-active' : ''}`}
            onClick={() => setSection('orders')}
          >
            Orders
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={section === 'checkout-links'}
            className={`sl-section-pill ${section === 'checkout-links' ? 'is-active' : ''}`}
            onClick={() => setSection('checkout-links')}
          >
            Checkout links
          </button>
        </div>

        {section === 'checkout-links' && (
          <CheckoutLinksTab
            key={`checkout-links-${createdLinksBump}`}
            onOpenSend={handleOpenSend}
          />
        )}

        {section === 'orders' && (<>
        {/* Stat strip */}
        <section className="rsord__stats">
          <div className="rsord-stat">
            <span className="rsord-stat__label">Total orders</span>
            <span className="rsord-stat__value">{summaryMetrics.totalOrders}</span>
          </div>
          <div className="rsord-stat">
            <span className="rsord-stat__label">Pending</span>
            <span className="rsord-stat__value">{summaryMetrics.pendingOrders}</span>
          </div>
          <div className="rsord-stat">
            <span className="rsord-stat__label">Completed</span>
            <span className="rsord-stat__value">{summaryMetrics.completedOrders}</span>
          </div>
          <div className="rsord-stat">
            <span className="rsord-stat__label">Revenue</span>
            <span className="rsord-stat__value">{formatInDisplayCurrency(summaryMetrics.totalRevenue, accountCurrency)}</span>
          </div>
        </section>

        {/* Tab strip */}
        <nav className="rsord__tabs" role="tablist">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`rsord__tab ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="rsord__tab-label">{tab.label}</span>
              <span className="rsord__tab-count">{statusCounts[tab.key] || 0}</span>
            </button>
          ))}
        </nav>

        {/* Toolbar: search + filter */}
        <div className="rsord__toolbar">
          <label className="rsord__search">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="rsord__search-input"
              placeholder="Search by order ID, customer, or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="rsord__search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </label>

          <button
            type="button"
            className={`rsord__btn rsord__btn--ghost ${showFilters || hasActiveFilters ? 'is-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span className="rsord__btn-label">Filter</span>
            {hasActiveFilters && <span className="rsord__filter-dot" aria-hidden />}
          </button>
        </div>

        {showFilters && (
          <div className="rsord__filters">
            <label className="rsord__filter">
              <span>Channel</span>
              <select
                value={channelFilter}
                onChange={(e) => {
                  setChannelFilter(e.target.value);
                  if (e.target.value !== 'MANUAL') setSourceFilter('all');
                }}
              >
                <option value="all">All channels</option>
                <option value="STOREFRONT">Storefront</option>
                <option value="POS">POS</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="MANUAL">Manual</option>
                <option value="APPOINTMENT">Appointments</option>
              </select>
            </label>

            {channelFilter === 'MANUAL' && (
              <label className="rsord__filter">
                <span>Source</span>
                <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                  <option value="all">All sources</option>
                  <option value="PHONE_CALL">Phone call</option>
                  <option value="SOCIAL_MEDIA_DM">Social media DM</option>
                  <option value="WALK_IN">Walk-in</option>
                  <option value="MARKET_STALL">Market/Pop-up</option>
                  <option value="EMAIL">Email</option>
                  <option value="REFERRAL">Referral</option>
                  <option value="WHOLESALE">Wholesale</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>
            )}

            {hasActiveFilters && (
              <button type="button" className="rsord__filter-clear" onClick={() => { setChannelFilter('all'); setSourceFilter('all'); }}>
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* List */}
        <section className="rsord__list">
          {status === 'loading' && (
            <div className="rsord__state">
              <span className="rsord__spinner rsord__spinner--lg" />
              <span>Loading orders…</span>
            </div>
          )}

          {error && (
            <div className="rsord__state rsord__state--error">
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {status === 'succeeded' && filteredOrders.length === 0 && (
            <div className="rsord__empty">
              <div className="rsord__empty-icon">
                <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                </svg>
              </div>
              <h3>No orders found</h3>
              <p>{searchQuery ? 'Try adjusting your search.' : `No ${activeTab === 'all' ? '' : activeTab} orders yet.`}</p>
            </div>
          )}

          {status === 'succeeded' && filteredOrders.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="rsord-table" role="table">
                <div className="rsord-table__head" role="row">
                  <div role="columnheader">Order</div>
                  <div role="columnheader">Type</div>
                  <div role="columnheader">Customer</div>
                  <div role="columnheader">Date</div>
                  <div role="columnheader">Status</div>
                  <div role="columnheader">Payment</div>
                  <div role="columnheader" className="rsord-table__num">Total</div>
                  <div role="columnheader" className="rsord-table__actions">Actions</div>
                </div>

                {filteredOrders.map(order => {
                  const customerName = order.customer?.firstName
                    ? `${order.customer.firstName} ${order.customer.lastName || ''}`.trim()
                    : 'Guest customer';
                  const itemCount = order.orderItems?.length || 0;
                  const isAppointment = order.type === 'appointment';
                  const channelLabel = order.channel === 'MANUAL' && order.manualSaleSource
                    ? order.manualSaleSource.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                    : (order.channel || 'Storefront').toLowerCase();
                  return (
                    <div
                      key={order.id}
                      role="row"
                      className="rsord-table__row"
                      onClick={() => handleViewOrder(order)}
                    >
                      <div className="rsord-table__cell" role="cell">
                        <div className="rsord-cell-order">
                          <span className="rsord-cell-order__id">#{order.id.slice(-8)}</span>
                          <span className="rsord-cell-order__meta">
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                          </span>
                        </div>
                      </div>

                      <div className="rsord-table__cell" role="cell">
                        <div className={`rsord-type ${isAppointment ? 'rsord-type--appointment' : 'rsord-type--order'}`}>
                          <span className="rsord-type__primary">
                            {isAppointment ? (
                              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            ) : (
                              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                              </svg>
                            )}
                            {isAppointment ? 'Appointment' : 'Order'}
                          </span>
                          {!isAppointment && <span className="rsord-type__channel">{channelLabel}</span>}
                        </div>
                      </div>

                      <div className="rsord-table__cell" role="cell">
                        <div className="rsord-cell-customer">
                          <div className="rsord-cell-customer__avatar">
                            {(order.customer?.firstName || 'G')[0].toUpperCase()}
                          </div>
                          <div className="rsord-cell-customer__info">
                            <span className="rsord-cell-customer__name">{customerName}</span>
                            {order.customer?.email && (
                              <span className="rsord-cell-customer__email">{order.customer.email}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rsord-table__cell rsord-table__cell--muted" role="cell">
                        {formatRelativeDate(order.createdAt)}
                      </div>

                      <div className="rsord-table__cell" role="cell">
                        <span className={`rsord-status rsord-status--${getStatusClass(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>

                      <div className="rsord-table__cell" role="cell">
                        <span className={`rsord-payment rsord-payment--${getPaymentClass(order.paymentStatus)}`}>
                          {order.paymentStatus ? getStatusLabel(order.paymentStatus) : 'Unpaid'}
                        </span>
                      </div>

                      <div className="rsord-table__cell rsord-table__num" role="cell">
                        <span className="rsord-cell-total">{formatInDisplayCurrency(order.total, accountCurrency)}</span>
                      </div>

                      <div className="rsord-table__cell rsord-table__actions" role="cell" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="rsord-icon-btn"
                          onClick={() => handleViewOrder(order)}
                          aria-label="View order"
                          title="View"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        {(order.paymentStatus || 'UNPAID').toUpperCase() === 'UNPAID' && !isAppointment && (
                          <button
                            type="button"
                            className="rsord-icon-btn"
                            onClick={(e) => openPaymentLinkPopover(order, e)}
                            aria-label="Send payment link"
                            title="Send payment link"
                          >
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          className="rsord-icon-btn"
                          onClick={() => handleUpdateOrder(order)}
                          aria-label="Edit order"
                          title="Edit"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile cards */}
              <ul className="rsord-mobile">
                {filteredOrders.map(order => {
                  const customerName = order.customer?.firstName
                    ? `${order.customer.firstName} ${order.customer.lastName || ''}`.trim()
                    : 'Guest customer';
                  const itemCount = order.orderItems?.length || 0;
                  const isAppointment = order.type === 'appointment';
                  const channelLabel = order.channel === 'MANUAL' && order.manualSaleSource
                    ? order.manualSaleSource.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                    : (order.channel || 'Storefront').toLowerCase();
                  return (
                    <li
                      key={order.id}
                      className="rsord-mcard"
                      onClick={() => handleViewOrder(order)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleViewOrder(order); }}
                    >
                      <div className="rsord-mcard__top">
                        <div>
                          <div className="rsord-mcard__id-row">
                            <span className="rsord-mcard__id">#{order.id.slice(-8)}</span>
                            <span className={`rsord-type__primary ${isAppointment ? 'rsord-type--appointment' : 'rsord-type--order'}`}>
                              {isAppointment ? 'Appointment' : 'Order'}
                            </span>
                          </div>
                          <div className="rsord-mcard__date">{formatRelativeDate(order.createdAt)}</div>
                        </div>
                        <span className={`rsord-status rsord-status--${getStatusClass(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>

                      <div className="rsord-mcard__customer">
                        <div className="rsord-cell-customer__avatar">
                          {(order.customer?.firstName || 'G')[0].toUpperCase()}
                        </div>
                        <div className="rsord-cell-customer__info">
                          <span className="rsord-cell-customer__name">{customerName}</span>
                          <span className="rsord-cell-customer__email">
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                            {!isAppointment && ` · ${channelLabel}`}
                          </span>
                        </div>
                      </div>

                      <div className="rsord-mcard__bottom">
                        <span className={`rsord-payment rsord-payment--${getPaymentClass(order.paymentStatus)}`}>
                          {order.paymentStatus ? getStatusLabel(order.paymentStatus) : 'Unpaid'}
                        </span>
                        <div className="rsord-mcard__bottom-right">
                          <span className="rsord-cell-total">{formatInDisplayCurrency(order.total, accountCurrency)}</span>
                          <button
                            type="button"
                            className="rsord-mcard__more"
                            onClick={(e) => openCardActionsMenu(order, e)}
                            aria-label="More actions"
                            aria-haspopup="menu"
                            aria-expanded={cardActionsMenu?.order?.id === order.id}
                          >
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <circle cx="12" cy="5.5" r="1.7" />
                              <circle cx="12" cy="12" r="1.7" />
                              <circle cx="12" cy="18.5" r="1.7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {totalPages > 1 && (
            <div className="rsord__pagination">
              <span className="rsord__pagination-info">
                Page {currentPage} of {totalPages} · {totalItems} {totalItems === 1 ? 'item' : 'items'}
              </span>
              <div className="rsord__pagination-controls">
                <button
                  type="button"
                  className="rsord__page-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  <span>Prev</span>
                </button>
                <span className="rsord__page-counter">
                  Page {currentPage} <em>of</em> {totalPages}
                </span>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`rsord__page-num ${currentPage === pageNum ? 'is-active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="rsord__page-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <span>Next</span>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </section>
        </>)}

        {showCreateOrderModal && (
          <CreateOrderModal
            isOpen={showCreateOrderModal}
            onClose={() => setShowCreateOrderModal(false)}
            onSuccess={handleCreateOrderSuccess}
          />
        )}

        {showRecordSaleModal && (
          <RecordSaleModal
            isOpen={showRecordSaleModal}
            onClose={() => setShowRecordSaleModal(false)}
            onSuccess={handleRecordSaleSuccess}
          />
        )}

        {/* Phase 3.2 — Send checkout link wizard (slide-in Sidebar) */}
        <SendCheckoutLinkSidebar
          isOpen={showSendSidebar}
          onClose={() => setShowSendSidebar(false)}
          onCreated={handleSendCreated}
        />

        {/* Phase 3.3 — Single shared Send-payment-link popover anchored
            to whichever row triggered it. */}
        <SendPaymentLinkPopover
          isOpen={!!paymentLinkPopover}
          onClose={closePaymentLinkPopover}
          anchorEl={paymentLinkPopover?.anchorEl || null}
          order={paymentLinkPopover?.order || null}
        />

        {/* Mobile-card actions menu — overflow popover anchored to the
            card's 3-dot trigger. Holds View / Edit / Send payment link
            so the card itself stays clean. */}
        <InlinePopover
          isOpen={!!cardActionsMenu}
          onClose={closeCardActionsMenu}
          anchorEl={cardActionsMenu?.anchorEl || null}
          width={220}
        >
          {cardActionsMenu?.order && (() => {
            const order = cardActionsMenu.order;
            const isUnpaid = (order.paymentStatus || 'UNPAID').toUpperCase() === 'UNPAID';
            const isAppt = order.type === 'appointment';
            const showSendLink = isUnpaid && !isAppt;
            return (
              <div className="rsord-mcard-menu">
                <p className="rsord-mcard-menu__head">Actions</p>
                <button
                  type="button"
                  className="rsord-mcard-menu__item"
                  onClick={() => { closeCardActionsMenu(); handleViewOrder(order); }}
                >
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <span>View order</span>
                </button>
                <button
                  type="button"
                  className="rsord-mcard-menu__item"
                  onClick={() => { closeCardActionsMenu(); handleUpdateOrder(order); }}
                >
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  <span>Edit order</span>
                </button>
                {showSendLink && (
                  <>
                    <div className="rsord-mcard-menu__divider" />
                    <button
                      type="button"
                      className="rsord-mcard-menu__item rsord-mcard-menu__item--accent"
                      onClick={() => {
                        const anchor = cardActionsMenu.anchorEl;
                        closeCardActionsMenu();
                        setPaymentLinkPopover({ order, anchorEl: anchor });
                      }}
                    >
                      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                      </svg>
                      <span>Send payment link</span>
                    </button>
                  </>
                )}
              </div>
            );
          })()}
        </InlinePopover>

      </div>
    </Shell>
  );
}
